import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { ExportStatusValue } from 'types/result';
import { Ordering, TimeRange } from 'screens/home/utils';
import { getDateRange } from 'widgets/home/utils';
import { AuthAction, AuthStatus, useAuth } from './AuthProvider';

export interface HomeFilterSet {
  searchQuery: string;
  dateRange: DateRange | undefined;
  ordering: Ordering;
  exportStatusFilter: ExportStatusValue | undefined;
}

const HOME_FILTERS_STORAGE_KEY = 'homeFilters';
const LOGIN_RESET_FLAG_KEY = 'homeFiltersResetOnLogin';

interface PersistedFilters {
  searchQuery: string;
  dateRange: { from?: string; to?: string } | undefined;
  ordering: Ordering;
  exportStatusFilter: ExportStatusValue | undefined;
}

function getDefaultFilters(): HomeFilterSet {
  return {
    searchQuery: '',
    dateRange: getDateRange(TimeRange.Today),
    ordering: Ordering.ZeitpunktDesc,
    exportStatusFilter: undefined,
  };
}

function loadFilters(): HomeFilterSet {
  try {
    const raw = localStorage.getItem(HOME_FILTERS_STORAGE_KEY);
    if (!raw) return getDefaultFilters();
    const p = JSON.parse(raw) as PersistedFilters;
    const validOrderings = Object.values(Ordering) as string[];
    const validExportStatuses = Object.values(ExportStatusValue) as string[];
    return {
      searchQuery: p.searchQuery ?? '',
      dateRange: p.dateRange
        ? {
            from: p.dateRange.from ? new Date(p.dateRange.from) : undefined,
            to: p.dateRange.to ? new Date(p.dateRange.to) : undefined,
          }
        : undefined,
      ordering: validOrderings.includes(p.ordering) ? p.ordering : Ordering.ZeitpunktDesc,
      exportStatusFilter:
        p.exportStatusFilter !== undefined && validExportStatuses.includes(p.exportStatusFilter)
          ? p.exportStatusFilter
          : undefined,
    };
  } catch {
    return getDefaultFilters();
  }
}

function saveFilters(filters: HomeFilterSet): void {
  try {
    const serializable: PersistedFilters = {
      searchQuery: filters.searchQuery,
      dateRange: filters.dateRange
        ? {
            from: filters.dateRange.from?.toISOString(),
            to: filters.dateRange.to?.toISOString(),
          }
        : undefined,
      ordering: filters.ordering,
      exportStatusFilter: filters.exportStatusFilter,
    };
    localStorage.setItem(HOME_FILTERS_STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // ignore storage errors
  }
}

type FiltersUpdater = HomeFilterSet | ((prev: HomeFilterSet) => HomeFilterSet);

interface HomeFiltersContextValue {
  filters: HomeFilterSet;
  setFilters: (next: FiltersUpdater) => void;
  resetFilters: () => void;
}

const HomeFiltersContext = createContext<HomeFiltersContextValue | undefined>(undefined);

export const HomeFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filters, setFiltersState] = useState<HomeFilterSet>(loadFilters);
  const { authStatus, lastAuthAction } = useAuth();

  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const setFilters = useCallback((next: FiltersUpdater) => {
    setFiltersState((prev) => (typeof next === 'function' ? next(prev) : next));
  }, []);

  useEffect(() => {
    const justLoggedIn =
      lastAuthAction === AuthAction.Login || lastAuthAction === AuthAction.EntraLogin;
    if (authStatus !== AuthStatus.Authenticated || !justLoggedIn) return;
    if (sessionStorage.getItem(LOGIN_RESET_FLAG_KEY)) return;

    sessionStorage.setItem(LOGIN_RESET_FLAG_KEY, 'true');
    setFilters((prev) => ({ ...prev, dateRange: getDateRange(TimeRange.Today) }));
  }, [authStatus, lastAuthAction, setFilters]);

  const resetFilters = useCallback(() => {
    setFiltersState(getDefaultFilters());
  }, []);

  const value = useMemo(
    () => ({ filters, setFilters, resetFilters }),
    [filters, setFilters, resetFilters]
  );

  return <HomeFiltersContext.Provider value={value}>{children}</HomeFiltersContext.Provider>;
};

export const useHomeFilters = (): HomeFiltersContextValue => {
  const context = useContext(HomeFiltersContext);
  if (!context) {
    throw new Error('useHomeFilters must be used within a HomeFiltersProvider');
  }
  return context;
};
